import { SbBlokData, storyblokEditable } from '@storyblok/react/rsc'
import { FC, Fragment, JSX } from 'react'
import { MARK_LINK, NODE_LI, NODE_PARAGRAPH, render } from 'storyblok-rich-text-react-renderer'

import Gridpanel from '../../02-molecules/Gridpanel/Blok'
import AccordionItem from '../Accordionitem/Blok'
import Button from '../Button/Blok'
import IFrame from '../IFrame/Blok'
import ImageHolder from '../ImageHolder/Blok'
import { PhnxImageHolder } from '../ImageHolder/ImageHolder'
import ReadMore from '../ReadMore/ReadMore'
import Table from '../Table/Blok'
import VideoHolder from '../VideoHolder/Blok'

interface RichTextBlokProps {
  blok: { content: any } & SbBlokData
}

const RichText: FC<RichTextBlokProps> = ({ blok }) => {
  const replaceYearPlaceholder = (text: string): string => {
    return text.replace(/\[\[year\]\]/g, new Date().getFullYear().toString())
  }

  const processTextContent = (content: any): any => {
    if (typeof content === 'string') {
      return replaceYearPlaceholder(content)
    }
    if (Array.isArray(content)) {
      return content.map(processTextContent)
    }
    if (content && typeof content === 'object') {
      const processed = { ...content }
      if (content.text) {
        processed.text = replaceYearPlaceholder(content.text)
      }
      if (content.content) {
        processed.content = processTextContent(content.content)
      }
      return processed
    }
    return content
  }

  const splitContentAtReadMore = (
    content: any
  ): Array<{ type: 'normal' | 'readmore'; content: any }> => {
    if (!content || !content.content || !Array.isArray(content.content)) {
      return [{ type: 'normal', content }]
    }

    const sections: Array<{ type: 'normal' | 'readmore'; content: any }> = []
    let currentSection: any[] = []
    let inReadMore = false
    let readMoreSection: any[] = []

    for (let i = 0; i < content.content.length; i++) {
      const node = content.content[i]
      let processedNode = { ...node }
      let foundStart = false
      let foundEnd = false

      if (node.content && Array.isArray(node.content)) {
        const newContent: any[] = []
        let textBeforeStart = ''
        let textInReadMore = ''
        let textAfterEnd = ''
        let hasTokens = false

        for (let j = 0; j < node.content.length; j++) {
          const textNode = node.content[j]

          if (textNode.text) {
            let remainingText = textNode.text

            // Check for [[readmore]] start tag
            if (remainingText.includes('[[readmore]]')) {
              hasTokens = true
              const parts = remainingText.split('[[readmore]]')
              textBeforeStart = parts[0]
              remainingText = parts.slice(1).join('[[readmore]]')
              foundStart = true
            }

            // Check for [[/readmore]] end tag
            if (remainingText.includes('[[/readmore]]')) {
              hasTokens = true
              const parts = remainingText.split('[[/readmore]]')
              textInReadMore = parts[0]
              textAfterEnd = parts.slice(1).join('[[/readmore]]')
              foundEnd = true
            } else if (foundStart) {
              textInReadMore = remainingText
            }

            if (!hasTokens) {
              newContent.push(textNode)
            }
          } else {
            newContent.push(textNode)
          }
        }

        if (hasTokens) {
          // Add text before [[readmore]]
          if (textBeforeStart) {
            if (currentSection.length === 0 && sections.length === 0) {
              currentSection.push({
                ...processedNode,
                content: [{ ...node.content[0], text: textBeforeStart }],
              })
            } else {
              currentSection.push({
                ...processedNode,
                content: [{ ...node.content[0], text: textBeforeStart }],
              })
            }
          }

          // Save current section before starting readmore
          if (foundStart && currentSection.length > 0) {
            sections.push({ type: 'normal', content: { ...content, content: currentSection } })
            currentSection = []
            inReadMore = true
          }

          // Add text inside readmore section
          if (textInReadMore && foundStart) {
            readMoreSection.push({
              ...processedNode,
              content: [{ ...node.content[0], text: textInReadMore }],
            })
          }

          // Close readmore section
          if (foundEnd) {
            sections.push({ type: 'readmore', content: { ...content, content: readMoreSection } })
            readMoreSection = []
            inReadMore = false
          }

          // Add text after [[/readmore]]
          if (textAfterEnd) {
            currentSection.push({
              ...processedNode,
              content: [{ ...node.content[0], text: textAfterEnd }],
            })
          }

          continue
        }
      }

      // Add node to appropriate section
      if (inReadMore) {
        readMoreSection.push(processedNode)
      } else {
        currentSection.push(processedNode)
      }
    }

    // Add any remaining content
    if (currentSection.length > 0) {
      sections.push({ type: 'normal', content: { ...content, content: currentSection } })
    }

    // If we're still in a readmore section (no end tag), treat rest as readmore
    if (readMoreSection.length > 0) {
      sections.push({ type: 'readmore', content: { ...content, content: readMoreSection } })
    }

    return sections.length > 0 ? sections : [{ type: 'normal', content }]
  }

  const processedContent = processTextContent(blok.content)
  const sections = splitContentAtReadMore(processedContent)

  const renderOptions = {
    blokResolvers: {
      button: (props: any) => <Button blok={props} />,
      image_holder: (props: any) => <ImageHolder blok={props} />,
      grid_panel: (props: any) => <Gridpanel blok={props} />,
      video_holder: (props: any) => <VideoHolder blok={props} />,
      AccordionItem: (props: any) => <AccordionItem blok={props} />,
      iframe: (props: any) => <IFrame blok={props} />,
      basic_table: (props: any) => <Table blok={props} />,
    },
    defaultBlokResolver: (name: string, props: any) => (
      <div>
        <code>Missing blok resolver for blok type {name}.</code>
        <pre>
          <code>{JSON.stringify(props, undefined, 2)}</code>
        </pre>
      </div>
    ),
    nodeResolvers: {
      [NODE_LI]: (children: any) => {
        if (children == null) return <br />
        return <li>{children}</li>
      },
      [NODE_PARAGRAPH]: (children: any) => {
        if (children == null) return <br />
        if (Array.isArray(children) && children[0]?.type === 'img') {
          return (
            <PhnxImageHolder
              images={[
                {
                  src: children[0].props.src,
                  alt: children[0].props.alt,
                  focus: children[0].props.focus,
                },
              ]}
            />
          )
        }
        return <p>{children}</p>
      },
      bullet_list: (children: any) => {
        if (!Array.isArray(children)) return null

        const renderNestedList = (items: any[]): JSX.Element => (
          <ul>
            {items.map((item, index) => {
              const itemChildren = item.props.children
              const hasNestedList = itemChildren.some((child: any) => child.type === 'ul')

              return (
                <li key={index} className={hasNestedList ? 'flex flex-wrap' : ''}>
                  {itemChildren.map((child: any, childIndex: number) => {
                    switch (child.type) {
                      case 'p':
                        return (
                          <p key={childIndex} className="w-full">
                            {child.props.children}
                          </p>
                        )
                      case 'ul':
                        return renderNestedList(child.props.children)
                      default:
                        return child
                    }
                  })}
                </li>
              )
            })}
          </ul>
        )

        return renderNestedList(children)
      },
    },
    markResolvers: {
      [MARK_LINK]: (children: any, props: any) => {
        const { href, target, linktype } = props
        return (
          <a
            href={linktype === 'email' ? `mailto:${href}` : href}
            target={target}
            rel={target === '_blank' ? 'noopener noreferrer' : undefined}
          >
            {children}
          </a>
        )
      },
    },
  }

  return (
    <div {...storyblokEditable(blok)} className="richText">
      {sections.map((section, index) => {
        if (section.type === 'readmore') {
          return <ReadMore key={index}>{render(section.content, renderOptions)}</ReadMore>
        }
        return <Fragment key={index}>{render(section.content, renderOptions)}</Fragment>
      })}
    </div>
  )
}

export default RichText
